import { Box, Skeleton, styled, Tooltip } from '@mui/material';
import { BodyText } from '@/components/BodyText';
import { AssetFileTypeCount } from '@/api/generatedApi';
import { useGetAssetFileTypeCounts } from '@/hooks/api/assets/useGetAssetFileTypeCounts';

const MAX_FILE_TYPE_ROWS = 5;

const FileTypeRow = styled(Box)({
  display: 'flex',
  justifyContent: 'space-between',
  gap: '24px',
  cursor: 'default',
});

const CountRow = ({
  label,
  count,
  testId,
  extensions,
}: {
  label: string;
  count: number;
  testId: string;
  extensions: string;
}) => (
  <Tooltip title={extensions} placement='top-start'>
    <FileTypeRow data-testid={`${testId}-row`}>
      <BodyText color='text.secondary'>{label}</BodyText>
      <BodyText data-testid={testId}>{count.toLocaleString()}</BodyText>
    </FileTypeRow>
  </Tooltip>
);

const describeOtherExtensions = (foldedFileTypes: AssetFileTypeCount[], unrecognized: number) => {
  const extensions = [...new Set(foldedFileTypes.flatMap(({ extensions }) => extensions))].sort((a, b) =>
    a.localeCompare(b)
  );
  const unrecognizedNote = unrecognized > 0 ? [`${unrecognized.toLocaleString()} unrecognized`] : [];
  return [...extensions, ...unrecognizedNote].join(', ');
};

const splitTopFileTypes = (fileTypes: AssetFileTypeCount[], assetsTotal: number) => {
  const topFileTypes = fileTypes.slice(0, MAX_FILE_TYPE_ROWS);
  const shownCount = topFileTypes.reduce((total, { count }) => total + count, 0);
  return {
    topFileTypes,
    foldedFileTypes: fileTypes.slice(MAX_FILE_TYPE_ROWS),
    otherCount: Math.max(0, assetsTotal - shownCount),
  };
};

export const AssetFileTypesSection = ({
  assetsTotal,
  pollWhileIndexing,
}: {
  assetsTotal: number;
  pollWhileIndexing?: boolean;
}) => {
  const { data: fileTypeCounts, isLoading, isError } = useGetAssetFileTypeCounts({ pollWhileIndexing });

  if (assetsTotal === 0 && !isLoading && !isError) return;

  const isReady = !isError && !isLoading && fileTypeCounts;
  const { topFileTypes, foldedFileTypes, otherCount } = splitTopFileTypes(fileTypeCounts?.fileTypes ?? [], assetsTotal);

  return (
    <Box display='flex' flexDirection='column' gap='8px'>
      <BodyText color='text.secondary' fontSize='0.75rem' textTransform='uppercase' letterSpacing='0.06em'>
        By file type
      </BodyText>
      {isError && <BodyText color='text.secondary'>Failed to load file type counts.</BodyText>}
      {!isError && !isReady && <Skeleton variant='rounded' height={120} />}
      {isReady && (
        <Box display='flex' flexDirection='column' gap='8px'>
          {topFileTypes.map(({ fileType, name, count, extensions }) => (
            <CountRow
              key={fileType}
              label={name}
              count={count}
              testId={`file-type-count-${fileType}`}
              extensions={extensions.join(', ')}
            />
          ))}
          {otherCount > 0 && (
            <CountRow
              label='Other'
              count={otherCount}
              testId='file-type-count-other'
              extensions={describeOtherExtensions(foldedFileTypes, fileTypeCounts.unrecognized)}
            />
          )}
        </Box>
      )}
    </Box>
  );
};
